import { useState } from 'react';
import { X, User, Mail, Phone, MapPin, FileText, Plus, Trash2 } from 'react-feather';

function AddClientModal({ isOpen, onClose, onSave }) {
    const [formData, setFormData] = useState({
        name: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        services: [],
        source: 'Website',
        notes: []
    });
    const [newNote, setNewNote] = useState('');
    const [loading, setLoading] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const addNote = () => {
        if (newNote.trim()) {
            setFormData(prev => ({
                ...prev,
                notes: [...prev.notes, {
                    id: Date.now(),
                    text: newNote.trim(),
                    date: new Date().toISOString(),
                    type: 'general'
                }]
            }));
            setNewNote('');
        }
    };

    const removeNote = (noteId) => {
        setFormData(prev => ({
            ...prev,
            notes: prev.notes.filter(note => note.id !== noteId)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            await onSave(formData);
            // Reset form
            setFormData({
                name: '',
                lastName: '',
                email: '',
                phone: '',
                address: '',
                services: [],
                source: 'Website',
                notes: []
            });
            onClose();
        } catch (error) {
            console.error('Error saving client:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title d-flex align-items-center">
                            <User size={20} style={{ marginRight: '8px' }} />
                            Add New Client
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                        ></button>
                    </div>
                    
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            <div className="row g-3">
                                {/* Basic Information */}
                                <div className="col-12">
                                    <h6 className="text-primary mb-3">Basic Information</h6>
                                </div>
                                
                                <div className="col-md-6">
                                    <label className="form-label">
                                        <User size={14} style={{ marginRight: '4px' }} />
                                        First Name *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                
                                <div className="col-md-6">
                                    <label className="form-label">
                                        <User size={14} style={{ marginRight: '4px' }} />
                                        Last Name *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                
                                <div className="col-md-6">
                                    <label className="form-label">
                                        <Mail size={14} style={{ marginRight: '4px' }} />
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                
                                <div className="col-md-6">
                                    <label className="form-label">
                                        <Phone size={14} style={{ marginRight: '4px' }} />
                                        Phone
                                    </label>
                                    <input
                                        type="tel"
                                        className="form-control"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                
                                <div className="col-12">
                                    <label className="form-label">
                                        <MapPin size={14} style={{ marginRight: '4px' }} />
                                        Address
                                    </label>
                                    <textarea
                                        className="form-control"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        rows="2"
                                    />
                                </div>
                                
                                {/* Services and Status */}
                                <div className="col-12">
                                    <h6 className="text-primary mb-3">Services & Status</h6>
                                </div>
                                
                                <div className="col-12">
                                    <label className="form-label">
                                        <FileText size={14} style={{ marginRight: '4px' }} />
                                        Services & Status
                                    </label>
                                    <div className="border rounded p-3">
                                        <div className="row g-2 mb-3">
                                            <div className="col-md-6">
                                                <select
                                                    className="form-select"
                                                    value=""
                                                    onChange={(e) => {
                                                        if (e.target.value) {
                                                            const newService = {
                                                                id: Date.now(),
                                                                name: e.target.value,
                                                                status: 'Pending',
                                                                price: 0,
                                                                addedDate: new Date().toISOString(),
                                                                completedDate: null
                                                            };
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                services: [...prev.services, newService]
                                                            }));
                                                            e.target.value = '';
                                                        }
                                                    }}
                                                >
                                                    <option value="">Add a service...</option>
                                                    <option value="LLC Creation">LLC Creation</option>
                                                    <option value="Tax Preparation">Tax Preparation</option>
                                                    <option value="Business Formation">Business Formation</option>
                                                    <option value="Consultation">Consultation</option>
                                                    <option value="Investment Advice">Investment Advice</option>
                                                    <option value="Bookkeeping">Bookkeeping</option>
                                                    <option value="Tax Planning">Tax Planning</option>
                                                    <option value="Audit Support">Audit Support</option>
                                                    <option value="Individual Tax Return">Individual Tax Return (Form 1040)</option>
                                                    <option value="Business Tax Return">Business Tax Return</option>
                                                    <option value="EIN Application">EIN Application</option>
                                                    <option value="Operating Agreement">Operating Agreement</option>
                                                    <option value="Bank Account Setup">Bank Account Setup</option>
                                                    <option value="Compliance Guidance">Compliance Guidance</option>
                                                    <option value="Real Estate Investment">Real Estate Investment</option>
                                                    <option value="Stock Market Investment">Stock Market Investment</option>
                                                    <option value="Cryptocurrency Investment">Cryptocurrency Investment</option>
                                                    <option value="Retirement Planning">Retirement Planning</option>
                                                    <option value="Portfolio Management">Portfolio Management</option>
                                                    <option value="Risk Assessment">Risk Assessment</option>
                                                    <option value="Web Design">Web Design</option>
                                                    <option value="Marketing Services">Marketing Services</option>
                                                    <option value="Rental Property Tax">Rental Property Tax</option>
                                                    <option value="Self-Employment Tax">Self-Employment Tax</option>
                                                    <option value="W2 Processing">W2 Processing</option>
                                                    <option value="Interest & Dividend Tax">Interest & Dividend Tax</option>
                                                    <option value="LLC Renewal">LLC Renewal</option>
                                                    <option value="US Bank Account Opening">US Bank Account Opening</option>
                                                    <option value="Non-Resident Tax Return">Non-Resident Tax Return (Form 1040NR)</option>
                                                    <option value="Work & Travel Tax">Work & Travel Tax</option>
                                                    <option value="Tax Amendment">Tax Amendment (Form 1040X)</option>
                                                    <option value="Stimulus Check Recovery">Stimulus Check Recovery</option>
                                                    <option value="Stock Trading Tax">Stock Trading Tax</option>
                                                    <option value="Expatriate Tax Optimization">Expatriate Tax Optimization</option>
                                                    <option value="Foreign Account Reporting">Foreign Account Reporting (FBAR)</option>
                                                    <option value="Corporate Tax Return">Corporate Tax Return (Form 1120)</option>
                                                    <option value="Partnership Tax Return">Partnership Tax Return (Form 1065)</option>
                                                    <option value="S-Corporation Tax Return">S-Corporation Tax Return (Form 1120-S)</option>
                                                    <option value="Business Structure Optimization">Business Structure Optimization</option>
                                                    <option value="Foreign Ownership Reporting">Foreign Ownership Reporting (Form 5472)</option>
                                                    <option value="Foreign Corporation Reporting">Foreign Corporation Reporting</option>
                                                    <option value="K-1 Form Processing">K-1 Form Processing</option>
                                                    <option value="Brand & Logo Registration">Brand & Logo Registration</option>
                                                    <option value="Domain Registration">Domain Registration</option>
                                                    <option value="Web Application Development">Web Application Development</option>
                                                    <option value="Database Management">Database Management</option>
                                                    <option value="Technical Support">Technical Support</option>
                                                    <option value="E-commerce Development">E-commerce Development</option>
                                                    <option value="Cybersecurity">Cybersecurity</option>
                                                    <option value="Online Marketing">Online Marketing</option>
                                                    <option value="Email Newsletter">Email Newsletter</option>
                                                    <option value="IT Team Formation">IT Team Formation</option>
                                                    <option value="Small Capital Investment">Small Capital Investment</option>
                                                    <option value="Large Capital Investment">Large Capital Investment</option>
                                                    <option value="Investment Visa Advisory">Investment Visa Advisory</option>
                                                    <option value="Short-term Investment Plans">Short-term Investment Plans</option>
                                                    <option value="Medium-term Investment Plans">Medium-term Investment Plans</option>
                                                    <option value="Long-term Investment Plans">Long-term Investment Plans</option>
                                                </select>
                                            </div>
                                        </div>
                                        
                                        {/* Display Selected Services */}
                                        {formData.services.length > 0 && (
                                            <div className="space-y-2">
                                                {formData.services.map((service, index) => (
                                                    <div key={service.id} className="d-flex align-items-center p-2 bg-light rounded">
                                                        <div className="flex-grow-1">
                                                            <strong>{service.name}</strong>
                                                            <div className="d-flex align-items-center mt-1">
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm me-2"
                                                                    style={{ width: '100px' }}
                                                                    placeholder="Price"
                                                                    value={service.price || ''}
                                                                    onChange={(e) => {
                                                                        const updatedServices = [...formData.services];
                                                                        updatedServices[index].price = parseFloat(e.target.value) || 0;
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            services: updatedServices
                                                                        }));
                                                                    }}
                                                                />
                                                                <span className="text-muted">USD</span>
                                                            </div>
                                                            <small className="text-muted d-block">
                                                                Added: {new Date(service.addedDate).toLocaleDateString()}
                                                            </small>
                                                        </div>
                                                        <div className="d-flex align-items-center me-2">
                                                            <select
                                                                className="form-select form-select-sm me-2"
                                                                style={{ width: '120px' }}
                                                                value={service.status}
                                                                onChange={(e) => {
                                                                    const updatedServices = [...formData.services];
                                                                    updatedServices[index].status = e.target.value;
                                                                    if (e.target.value === 'Completed') {
                                                                        updatedServices[index].completedDate = new Date().toISOString();
                                                                    }
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        services: updatedServices
                                                                    }));
                                                                }}
                                                            >
                                                                <option value="Pending">Pending</option>
                                                                <option value="In Progress">In Progress</option>
                                                                <option value="Completed">Completed</option>
                                                                <option value="Cancelled">Cancelled</option>
                                                            </select>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        services: prev.services.filter((_, i) => i !== index)
                                                                    }));
                                                                }}
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="col-md-3">
                                    <label className="form-label">Source</label>
                                    <select
                                        className="form-select"
                                        name="source"
                                        value={formData.source}
                                        onChange={handleInputChange}
                                    >
                                        <option value="Website">Website</option>
                                        <option value="Referral">Referral</option>
                                        <option value="Social Media">Social Media</option>
                                        <option value="Direct Contact">Direct Contact</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                
                                {/* Notes Section */}
                                <div className="col-12">
                                    <h6 className="text-primary mb-3">Notes & Comments</h6>
                                </div>
                                
                                <div className="col-12">
                                    <div className="input-group mb-3">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Add a note about this client..."
                                            value={newNote}
                                            onChange={(e) => setNewNote(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addNote())}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary"
                                            onClick={addNote}
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Display Notes */}
                                {formData.notes.length > 0 && (
                                    <div className="col-12">
                                        <div className="border rounded p-3">
                                            <h6 className="mb-3">Client Notes</h6>
                                            {formData.notes.map((note) => (
                                                <div key={note.id} className="d-flex align-items-start mb-2 p-2 bg-light rounded">
                                                    <div className="flex-grow-1">
                                                        <p className="mb-1">{note.text}</p>
                                                        <small className="text-muted">
                                                            {new Date(note.date).toLocaleDateString()}
                                                        </small>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger ms-2"
                                                        onClick={() => removeNote(note.id)}
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading || !formData.name || !formData.lastName || !formData.email}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Saving...
                                    </>
                                ) : (
                                    'Save Client'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default AddClientModal; 